import type { KokoroModelState } from './types';
import type { KokoroVoiceId } from './voiceMapping';

const MODEL_ID = 'onnx-community/Kokoro-82M-v1.0-ONNX';
// Pinned to q8 + WASM to match the dtype/device combo that worked under
// Node during the reference test. WebGPU and q4f16 were producing garbled
// output in the browser even with crossOriginIsolated=true; forcing the
// WASM path isolates Kokoro's execution to the same backend family that
// produces clean audio under onnxruntime-node.
const DTYPE = 'q8';
const DEVICE = 'wasm';
const DEFAULT_SAMPLE_RATE = 24000;

type ProgressListener = (pct: number) => void;
type StateListener = (state: KokoroModelState) => void;

interface RawAudioLike {
  audio: Float32Array;
  sampling_rate: number;
  toBlob: () => Blob;
}

interface KokoroStreamChunk {
  text: string;
  phonemes: string;
  audio: RawAudioLike;
}

interface TokenizedInput {
  input_ids: { dims: number[] };
}

export interface KokoroTTSLike {
  generate: (
    text: string,
    opts: { voice: string },
  ) => Promise<RawAudioLike>;
  stream: (
    text: string,
    opts: { voice: string },
  ) => AsyncIterable<KokoroStreamChunk>;
  /** Stage 8: direct access to the underlying tokenizer, used by speakKokoroBlend. */
  tokenizer: (text: string, opts: { truncation?: boolean }) => TokenizedInput;
  /** Stage 8: direct model forward. */
  model: (inputs: {
    input_ids: unknown;
    style: unknown;
    speed: unknown;
  }) => Promise<{ waveform: { data: Float32Array } }>;
}

class KokoroClientImpl {
  private tts: KokoroTTSLike | null = null;
  private state: KokoroModelState = 'idle';
  private loadPromise: Promise<void> | null = null;
  private synthQueue: Promise<unknown> = Promise.resolve();
  private progressListeners = new Set<ProgressListener>();
  private stateListeners = new Set<StateListener>();
  private lastProgress = 0;

  getState(): KokoroModelState {
    return this.state;
  }

  getProgress(): number {
    return this.lastProgress;
  }

  /** Stage 8: expose the loaded KokoroTTS instance so speakKokoroBlend can
   *  drive the model + tokenizer directly with a custom style vector. */
  getTts(): KokoroTTSLike | null {
    return this.tts;
  }

  onProgress(cb: ProgressListener): () => void {
    this.progressListeners.add(cb);
    return () => {
      this.progressListeners.delete(cb);
    };
  }

  onState(cb: StateListener): () => void {
    this.stateListeners.add(cb);
    return () => {
      this.stateListeners.delete(cb);
    };
  }

  private setState(s: KokoroModelState): void {
    this.state = s;
    for (const l of this.stateListeners) l(s);
  }

  private emitProgress(pct: number): void {
    this.lastProgress = pct;
    for (const l of this.progressListeners) l(pct);
  }

  isReady(): boolean {
    return this.state === 'ready' && this.tts !== null;
  }

  async waitUntilReady(): Promise<void> {
    if (this.isReady()) return;
    await this.ensureReady();
  }

  async ensureReady(): Promise<void> {
    if (this.state === 'ready' && this.tts) return;
    if (this.loadPromise) return this.loadPromise;
    this.setState('loading');
    this.loadPromise = (async () => {
      try {
        // Dynamic import so bundle size for non-Kokoro users stays flat.
        const mod = await import('kokoro-js');
        const KokoroTTS = (mod as { KokoroTTS: {
          from_pretrained: (
            id: string,
            opts: Record<string, unknown>,
          ) => Promise<KokoroTTSLike>;
        } }).KokoroTTS;

        this.tts = await KokoroTTS.from_pretrained(MODEL_ID, {
          dtype: DTYPE,
          device: DEVICE,
          progress_callback: (p: unknown) => {
            const obj = p as { status?: string; progress?: number };
            if (obj?.status === 'progress' && typeof obj.progress === 'number') {
              const pct = Math.round(obj.progress);
              this.emitProgress(pct);
            } else if (obj?.status === 'done') {
              this.emitProgress(100);
            }
          },
        });
        this.emitProgress(100);
        this.setState('ready');
      } catch (err) {
        this.setState('error');
        throw err;
      } finally {
        this.loadPromise = null;
      }
    })();
    return this.loadPromise;
  }

  async synthesize(opts: {
    text: string;
    voiceId: KokoroVoiceId;
  }): Promise<{ wav: Blob; durationMs: number; sizeBytes: number; sampleRate: number }> {
    await this.ensureReady();
    if (!this.tts) throw new Error('Kokoro not initialised');

    // Serialise synth calls — onnxruntime-web sessions are not re-entrant.
    const prev = this.synthQueue;
    const run = prev.then(async () => {
      const out = await this.tts!.generate(opts.text, { voice: opts.voiceId });
      const sr = out.sampling_rate || DEFAULT_SAMPLE_RATE;
      const wav = out.toBlob();
      const durationMs = Math.round((out.audio.length / sr) * 1000);

      // Fire-and-forget diagnostic: log the phonemes the espeak-ng WASM
      // produced for this text + voice. Doesn't block synthesis. If the
      // phonemizer is broken in the bundle, this line reveals it.
      void debugLogPhonemes(opts.text, opts.voiceId).catch(() => {
        /* swallow — diagnostic only */
      });

      return { wav, durationMs, sizeBytes: wav.size, sampleRate: sr };
    });
    // Keep the queue resolved regardless of failures.
    this.synthQueue = run.then(
      () => undefined,
      () => undefined,
    );
    return run;
  }

  async clearCache(): Promise<void> {
    if (typeof caches !== 'undefined') {
      const keys = await caches.keys();
      await Promise.all(
        keys
          .filter(
            (k) =>
              k.startsWith('transformers-') ||
              k.startsWith('zerotts-atoms-') ||
              k === 'zerotts-manifests',
          )
          .map((k) => caches.delete(k)),
      );
    }
    this.tts = null;
    this.lastProgress = 0;
    this.setState('idle');
  }
}

export const kokoroClient = new KokoroClientImpl();

/**
 * Diagnostic-only: calls the underlying espeak-ng WASM phonemizer directly
 * (the same dependency kokoro-js uses) and logs the IPA phonemes to the
 * browser console. Voice prefix 'a*' → en-us, 'b*' → en-gb.
 *
 * If this line prints valid IPA (`həlˈoʊ ...`) the phonemizer is OK and any
 * unintelligibility is downstream (model / voice style vector / WAV). If it
 * prints an empty string, gibberish, or throws, the phonemizer WASM isn't
 * running correctly in the Vite bundle.
 */
async function debugLogPhonemes(
  text: string,
  voiceId: string,
): Promise<void> {
  try {
    const mod = (await import('phonemizer')) as unknown as {
      phonemize: (text: string, lang?: string) => Promise<string[]>;
    };
    const lang = voiceId.startsWith('b') ? 'en-gb' : 'en-us';
    const phones = await mod.phonemize(text, lang);
    // eslint-disable-next-line no-console
    console.info(
      `[kokoro] voice=${voiceId} lang=${lang} phonemes=${JSON.stringify(phones)}`,
    );
  } catch (err) {
    // eslint-disable-next-line no-console
    console.warn('[kokoro] phonemizer diagnostic failed:', err);
  }
}
